class PredictiveSearch extends HTMLElement {
  constructor () {
    super()
    this.cachedResults = {}
    this.input = this.querySelector('input[type="search"]')
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]')
    this.isOpen = false
    this.intentionalBlur = false
    this.setupEventListeners()
  }

  setupEventListeners () {
    const form = this.querySelector('form.search')
    form.addEventListener('submit', this.onFormSubmit.bind(this))
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event)
    }, 300).bind(this))
    this.input.addEventListener('focus', this.onFocus.bind(this))
    this.input.addEventListener('blur', this.onBlur.bind(this))
    this.addEventListener('focusout', this.onFocusOut.bind(this))
    this.addEventListener('keyup', this.onKeyup.bind(this))
    this.addEventListener('keydown', this.onKeydown.bind(this))
  }

  getQuery () {
    return this.input.value.trim()
  }

  onChange () {
    const searchTerm = this.getQuery()
    if (!searchTerm.length) {
      this.close(true)
      return
    }
    this.getSearchResults(searchTerm)
  }

  onFormSubmit (event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault()
  }

  onFocus () {
    const searchTerm = this.getQuery()
    if (!searchTerm.length) return
    if (this.getAttribute('results') === 'true') {
      this.open()
    } else {
      this.getSearchResults(searchTerm)
    }
  }

  onBlur () {
    if (window.innerWidth < 768) {
      this.input.blur()
    }
  }

  onFocusOut () {
    setTimeout(() => {
      if (!this.contains(document.activeElement) && !this.intentionalBlur) {
        this.close()
      }
    })
  }

  onKeyup (event) {
    if (!this.getQuery().length) this.close(true)
    event.preventDefault()
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break
      case 'ArrowDown':
        this.switchOption('down')
        break
      case 'Enter':
        this.selectOption()
        break
    }
  }

  onKeydown (event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault()
    }
  }

  onTouchStart (event) {
    if (this.getAttribute('open') === 'true' && window.innerWidth < 768) {
      const searchModal = document.getElementById('tb_header_menu_search_modal')
      if (searchModal && !searchModal.contains(event.target)) {
        this.close()
      }
    }
  }

  onResultsScroll () {
    if (window.innerWidth < 768 && this.input === document.activeElement) {
      this.intentionalBlur = true
      this.input.blur()
      setTimeout(() => {
        this.intentionalBlur = false
      }, 100)
    }
  }

  switchOption (direction) {
    if (!this.getAttribute('open')) return
    const moveUp = direction === 'up'
    const selectedElement = this.querySelector('[aria-selected="true"]')
    const allElements = this.querySelectorAll('.predictive-search__result-item, .predictive-search__view-all-item, .predictive-search__roof-bar-cta-button')
    let activeElement = this.querySelector('.predictive-search__result-item, .predictive-search__view-all-item, .predictive-search__roof-bar-cta-button')
    if (moveUp && !selectedElement) return
    this.statusElement.textContent = ''
    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0]
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1]
    }
    if (activeElement === selectedElement) return
    activeElement.setAttribute('aria-selected', true)
    if (selectedElement) selectedElement.setAttribute('aria-selected', false)
    this.setLiveRegionText(activeElement.textContent)
    this.input.setAttribute('aria-activedescendant', activeElement.id)
  }

  selectOption () {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')
    if (selectedProduct) selectedProduct.click()
  }

  getSearchResults (searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase()
    this.setLiveRegionLoadingState()
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    const basePath = (window?.Shopify?.routes?.root || '/')
    fetch(`${basePath}search/suggest?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status)
          this.close()
          throw error
        }
        return response.text()
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML
        this.cachedResults[queryKey] = resultsMarkup
        this.renderSearchResults(resultsMarkup)
      })
      .catch((error) => {
        this.close()
        throw error
      })
  }

  setLiveRegionLoadingState () {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status')
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text')
    this.setLiveRegionText(this.loadingText)
    this.setAttribute('loading', true)
  }

  setLiveRegionText (statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false')
    this.statusElement.textContent = statusText
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true')
    }, 1000)
  }

  renderSearchResults (resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup
    this.setAttribute('results', true)
    this.setLiveRegionResults()
    this.open()
  }

  setLiveRegionResults () {
    this.removeAttribute('loading')
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent)
  }

  getResultsMaxHeight () {
    const header = document.getElementById('shopify-section-header')
    const searchForm = this.querySelector('form.search')
    const headerHeight = header ? header.getBoundingClientRect().height : 60
    const formHeight = searchForm ? searchForm.offsetHeight : 80
    const availableHeight = window.innerHeight * (window.innerWidth < 768 ? 0.9 : 1)
    this.resultsMaxHeight = availableHeight - headerHeight - formHeight
    return Math.max(this.resultsMaxHeight, 200)
  }

  open () {
    const scrollableContainer = this.querySelector('#predictive-search-results')
    if (scrollableContainer) {
      scrollableContainer.style.maxHeight = `${this.getResultsMaxHeight()}px`
    }
    if (window.innerWidth < 768) {
      this.scrollY = window.scrollY
      document.body.classList.add('overflow-hidden', 'fixed', 'w-full')
      document.body.style.top = `-${this.scrollY}px`
    }
    this.setAttribute('open', true)
    this.input.setAttribute('aria-expanded', true)
    if (window.innerWidth < 768) {
      this.boundTouchStart = this.onTouchStart.bind(this)
      document.addEventListener('touchstart', this.boundTouchStart)
      const scrollableContainer = this.querySelector('#predictive-search-results')
      if (scrollableContainer) {
        this.boundScrollHandler = this.onResultsScroll.bind(this)
        scrollableContainer.addEventListener('scroll', this.boundScrollHandler)
      }
    }
    this.isOpen = true
  }

  close (clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = ''
      this.removeAttribute('results')
    }
    const selected = this.querySelector('[aria-selected="true"]')
    if (selected) selected.setAttribute('aria-selected', false)
    this.input.setAttribute('aria-activedescendant', '')
    this.removeAttribute('open')
    this.input.setAttribute('aria-expanded', false)
    this.resultsMaxHeight = false
    document.body.classList.remove('overflow-hidden', 'fixed', 'w-full')
    document.body.style.top = ''
    if (this.scrollY) {
      window.scrollTo(0, this.scrollY)
      this.scrollY = null
    }
    if (this.boundTouchStart) {
      document.removeEventListener('touchstart', this.boundTouchStart)
      this.boundTouchStart = null
    }
    if (this.boundScrollHandler) {
      const scrollableContainer = this.querySelector('#predictive-search-results')
      if (scrollableContainer) {
        scrollableContainer.removeEventListener('scroll', this.boundScrollHandler)
      }
      this.boundScrollHandler = null
    }
    const scrollableContainer = this.querySelector('#predictive-search-results')
    if (scrollableContainer) {
      scrollableContainer.removeAttribute('style')
    }
    this.isOpen = false
  }
}
customElements.define('predictive-search', PredictiveSearch)
